import React, { useEffect, useState } from "react";
import { Button, Divider, Input, Modal, Table, Tooltip } from "antd";
import { EditOutlined, PlusOutlined, SearchOutlined } from "@ant-design/icons";
import SuperAdminContainer from "../SuperAdminContainer";
import ScreenHeader from "../../../CommonComponents/ScreenHeader";
import ChantButton from "../../../CommonComponents/ChantButton/ChantButton";
import {
  createClubColors,
  fetchClubColors,
  saveClubColors,
} from "../../../services/firebaseService/endPoints/clubColors";
import logger from "../../../utils/logger";

// ─── Constants ───────────────────────────────────────────────────────────────

const COLOR_KEYS = [
  "color1",
  "color2",
  "color3",
  "color4",
  "color5",
  "color6",
  "color7",
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

function isValidColor(c) {
  return c && c.r !== "" && c.g !== "" && c.b !== "";
}

function toRgbStr(c) {
  return `rgb(${c.r}, ${c.g}, ${c.b})`;
}

function rgbToHex(c) {
  const h = (v) => Number(v).toString(16).padStart(2, "0");
  return `#${h(c.r)}${h(c.g)}${h(c.b)}`;
}

function hexToRgb(hex) {
  return {
    r: parseInt(hex.slice(1, 3), 16),
    g: parseInt(hex.slice(3, 5), 16),
    b: parseInt(hex.slice(5, 7), 16),
  };
}

const emptyColor = () => ({ r: "", g: "", b: "" });

function initColors(record) {
  const state = {};
  COLOR_KEYS.forEach((key) => {
    state[key] =
      record && isValidColor(record[key]) ? { ...record[key] } : emptyColor();
  });
  return state;
}

function colorsEqual(a, b) {
  const r = (c) => ({ r: c?.r ?? "", g: c?.g ?? "", b: c?.b ?? "" });
  const na = r(a);
  const nb = r(b);
  return String(na.r) === String(nb.r) && String(na.g) === String(nb.g) && String(na.b) === String(nb.b);
}

function getDiff(original, edited) {
  return COLOR_KEYS.filter((key) => {
    const orig = (original && original[key]) || emptyColor();
    return !colorsEqual(orig, edited[key]);
  });
}

// ─── Sub-components ───────────────────────────────────────────────────────────

const Swatch = ({ color, size = 28 }) => (
  <div
    title={`rgb(${color.r}, ${color.g}, ${color.b})`}
    style={{
      width: size,
      height: size,
      borderRadius: 4,
      backgroundColor: toRgbStr(color),
      border: "1px solid rgba(0,0,0,0.15)",
      flexShrink: 0,
    }}
  />
);

const ColorRow = ({ label, color, onChange, onClear, onAdd }) => {
  const valid = isValidColor(color);
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "10px 0",
        borderBottom: "1px solid #f0f0f0",
      }}
    >
      <span style={{ width: 56, flexShrink: 0, color: "#555", fontSize: 13 }}>
        {label}
      </span>

      {valid ? (
        <>
          <Swatch color={color} />
          <input
            type="color"
            value={rgbToHex(color)}
            onChange={(e) => onChange(hexToRgb(e.target.value))}
            style={{
              width: 38,
              height: 30,
              border: "1px solid #d9d9d9",
              borderRadius: 4,
              padding: 2,
              cursor: "pointer",
              flexShrink: 0,
            }}
          />
          <span
            style={{ fontSize: 12, color: "#888", fontFamily: "monospace" }}
          >
            {rgbToHex(color).toUpperCase()}
          </span>
          <span
            style={{ fontSize: 12, color: "#aaa", fontFamily: "monospace" }}
          >
            rgb({color.r}, {color.g}, {color.b})
          </span>
          <Button
            type="link"
            danger
            size="small"
            onClick={onClear}
            style={{ marginLeft: "auto", padding: 0, flexShrink: 0 }}
          >
            Remove
          </Button>
        </>
      ) : (
        <>
          <div
            style={{
              width: 28,
              height: 28,
              borderRadius: 4,
              border: "1px dashed #d9d9d9",
              background: "#fafafa",
              flexShrink: 0,
            }}
          />
          <span style={{ color: "#bbb", fontSize: 13 }}>Not set</span>
          <Button
            type="link"
            size="small"
            onClick={onAdd}
            style={{ marginLeft: "auto", padding: 0, flexShrink: 0 }}
          >
            + Add
          </Button>
        </>
      )}
    </div>
  );
};

const DiffRow = ({ label, before, after }) => {
  const wasBefore = isValidColor(before);
  const isAfter = isValidColor(after);
  const changeLabel =
    !wasBefore && isAfter
      ? "Added"
      : wasBefore && !isAfter
        ? "Removed"
        : "Changed";

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "8px 0",
        borderBottom: "1px solid #f0f0f0",
      }}
    >
      <span style={{ width: 56, flexShrink: 0, color: "#555", fontSize: 13 }}>
        {label}
      </span>
      {wasBefore ? (
        <Swatch color={before} size={24} />
      ) : (
        <div
          style={{
            width: 24,
            height: 24,
            borderRadius: 4,
            border: "1px dashed #d9d9d9",
            background: "#fafafa",
          }}
        />
      )}
      <span style={{ color: "#bbb", fontSize: 13 }}>→</span>
      {isAfter ? (
        <Swatch color={after} size={24} />
      ) : (
        <div
          style={{
            width: 24,
            height: 24,
            borderRadius: 4,
            border: "1px dashed #d9d9d9",
            background: "#fafafa",
          }}
        />
      )}
      <span style={{ fontSize: 12, color: "#888" }}>{changeLabel}</span>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const Colors = () => {
  const [tableData, setTableData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [search, setSearch] = useState("");

  // Edit
  const [editRecord, setEditRecord] = useState(null);
  const [editColors, setEditColors] = useState({});
  const [showEditModal, setShowEditModal] = useState(false);
  const [showEditConfirm, setShowEditConfirm] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);

  // Create
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState({ clubName: "", clubId: "" });
  const [createColors, setCreateColors] = useState({});
  const [showCreateConfirm, setShowCreateConfirm] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);

  // ── Data ──

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const data = await fetchClubColors();
      data.sort((a, b) => (a.clubName || "").localeCompare(b.clubName || ""));
      setTableData(data);
    } catch (error) {
      logger.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // ── Edit ──

  const openEdit = (record) => {
    setEditRecord(record);
    setEditColors(initColors(record));
    setShowEditModal(true);
  };

  const closeEdit = () => {
    setShowEditModal(false);
    setEditRecord(null);
  };

  const editDiff = editRecord ? getDiff(editRecord, editColors) : [];

  const handleEditSave = () => {
    if (editDiff.length === 0) {
      closeEdit();
      return;
    }
    setShowEditConfirm(true);
  };

  const handleConfirmEdit = async () => {
    try {
      setSaveLoading(true);
      await saveClubColors(editRecord.id, { ...editRecord, ...editColors });
      setShowEditConfirm(false);
      closeEdit();
      await fetchData();
    } catch (error) {
      logger.error(error);
    } finally {
      setSaveLoading(false);
    }
  };

  // ── Create ──

  const openCreate = () => {
    setCreateForm({ clubName: "", clubId: "" });
    setCreateColors(initColors(null));
    setShowCreateModal(true);
  };

  const closeCreate = () => {
    setShowCreateModal(false);
  };

  const newColors = Object.values(createColors).filter(isValidColor);

  const handleConfirmCreate = async () => {
    try {
      setCreateLoading(true);
      await createClubColors({ ...createForm, ...createColors });
      setShowCreateConfirm(false);
      closeCreate();
      await fetchData();
    } catch (error) {
      logger.error(error);
    } finally {
      setCreateLoading(false);
    }
  };

  // ── Table columns ──

  const columns = [
    {
      title: "",
      key: "actions",
      width: 50,
      align: "center",
      render: (record) => (
        <Tooltip title="Edit">
          <Button
            icon={<EditOutlined style={{ fontSize: 18 }} />}
            type="text"
            onClick={() => openEdit(record)}
          />
        </Tooltip>
      ),
    },
    {
      title: "Club Name",
      dataIndex: "clubName",
      key: "clubName",
      width: 200,
      sorter: (a, b) => (a.clubName || "").localeCompare(b.clubName || ""),
      defaultSortOrder: "ascend",
      className: "header-white",
    },
    {
      title: "Club ID",
      dataIndex: "clubId",
      key: "clubId",
      width: 80,
      className: "header-white",
    },
    ...COLOR_KEYS.map((key, i) => ({
      title: `Color ${i + 1}`,
      key,
      width: 80,
      className: "header-white",
      render: (rec) =>
        isValidColor(rec[key]) ? <Swatch color={rec[key]} size={28} /> : null,
    })),
  ];

  return (
    <SuperAdminContainer>
      <ScreenHeader
        title="Colors"
        elementCount={tableData.length}
        actions={
          <ChantButton onClick={openCreate}>
            <PlusOutlined /> New
          </ChantButton>
        }
      />

      <div
        style={{
          backgroundColor: "var(--primary-dashboard-background)",
          marginTop: "2px",
          padding: "12px",
        }}
      >
        <Input
          prefix={<SearchOutlined style={{ color: "#bbb" }} />}
          placeholder="Search by club name or club ID..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          allowClear
          style={{ maxWidth: 320 }}
        />
      </div>

      <Table
        bordered
        columns={columns}
        dataSource={tableData.filter((row) => {
          const q = search.trim().toLowerCase();
          if (!q) return true;
          return (
            (row.clubName || "").toLowerCase().includes(q) ||
            String(row.clubId || "").toLowerCase().includes(q)
          );
        })}
        loading={isLoading}
        rowKey="id"
        scroll={{ x: "max-content" }}
        pagination={{
          defaultPageSize: 100,
          showSizeChanger: true,
          pageSizeOptions: [10, 20, 50, 100],
          position: ["bottomLeft"],
        }}
      />

      {/* ── Edit Modal ── */}
      <Modal
        title={`Edit Colors — ${editRecord?.clubName}`}
        open={showEditModal}
        onCancel={closeEdit}
        footer={[
          <Button key="cancel" onClick={closeEdit}>
            Cancel
          </Button>,
          <Button key="save" type="primary" onClick={handleEditSave}>
            Save
          </Button>,
        ]}
        width={580}
        centered
        destroyOnClose
      >
        {COLOR_KEYS.map((key, i) => (
          <ColorRow
            key={key}
            label={`Color ${i + 1}`}
            color={editColors[key] || emptyColor()}
            onChange={(rgb) => setEditColors((p) => ({ ...p, [key]: rgb }))}
            onClear={() =>
              setEditColors((p) => ({ ...p, [key]: emptyColor() }))
            }
            onAdd={() =>
              setEditColors((p) => ({
                ...p,
                [key]: { r: 255, g: 255, b: 255 },
              }))
            }
          />
        ))}
      </Modal>

      {/* ── Edit Confirm Modal ── */}
      <Modal
        title="Confirm Changes"
        open={showEditConfirm}
        onCancel={() => setShowEditConfirm(false)}
        footer={[
          <Button key="cancel" onClick={() => setShowEditConfirm(false)}>
            Cancel
          </Button>,
          <Button
            key="confirm"
            type="primary"
            loading={saveLoading}
            onClick={handleConfirmEdit}
          >
            Confirm & Save
          </Button>,
        ]}
        width={400}
        centered
        destroyOnClose
      >
        <p style={{ color: "#666", marginBottom: 12 }}>
          The following changes will be saved for{" "}
          <strong>{editRecord?.clubName}</strong>:
        </p>
        {editRecord &&
          editDiff.map((key) => (
            <DiffRow
              key={key}
              label={`Color ${COLOR_KEYS.indexOf(key) + 1}`}
              before={editRecord[key] || emptyColor()}
              after={editColors[key]}
            />
          ))}
      </Modal>

      {/* ── Create Modal ── */}
      <Modal
        title="Add Club Colors"
        open={showCreateModal}
        onCancel={closeCreate}
        footer={[
          <Button key="cancel" onClick={closeCreate}>
            Cancel
          </Button>,
          <Button
            key="save"
            type="primary"
            disabled={!createForm.clubName || !createForm.clubId}
            onClick={() => setShowCreateConfirm(true)}
          >
            Save
          </Button>,
        ]}
        width={580}
        centered
        destroyOnClose
      >
        <div style={{ display: "flex", gap: 16, marginBottom: 20 }}>
          <div style={{ flex: 1 }}>
            <label
              style={{
                fontSize: 13,
                color: "#555",
                display: "block",
                marginBottom: 4,
              }}
            >
              Club Name
            </label>
            <Input
              value={createForm.clubName}
              onChange={(e) =>
                setCreateForm((p) => ({ ...p, clubName: e.target.value }))
              }
              placeholder="e.g. England"
            />
          </div>
          <div style={{ width: 120 }}>
            <label
              style={{
                fontSize: 13,
                color: "#555",
                display: "block",
                marginBottom: 4,
              }}
            >
              Club ID
            </label>
            <Input
              value={createForm.clubId}
              onChange={(e) =>
                setCreateForm((p) => ({ ...p, clubId: e.target.value }))
              }
              placeholder="e.g. 10"
            />
          </div>
        </div>
        <Divider style={{ margin: "0 0 4px" }} />
        {COLOR_KEYS.map((key, i) => (
          <ColorRow
            key={key}
            label={`Color ${i + 1}`}
            color={createColors[key] || emptyColor()}
            onChange={(rgb) => setCreateColors((p) => ({ ...p, [key]: rgb }))}
            onClear={() =>
              setCreateColors((p) => ({ ...p, [key]: emptyColor() }))
            }
            onAdd={() =>
              setCreateColors((p) => ({
                ...p,
                [key]: { r: 255, g: 255, b: 255 },
              }))
            }
          />
        ))}
      </Modal>

      {/* ── Create Confirm Modal ── */}
      <Modal
        title="Confirm New Entry"
        open={showCreateConfirm}
        onCancel={() => setShowCreateConfirm(false)}
        footer={[
          <Button key="cancel" onClick={() => setShowCreateConfirm(false)}>
            Cancel
          </Button>,
          <Button
            key="confirm"
            type="primary"
            loading={createLoading}
            onClick={handleConfirmCreate}
          >
            Confirm & Save
          </Button>,
        ]}
        width={400}
        centered
        destroyOnClose
      >
        <p style={{ color: "#666", marginBottom: 12 }}>
          Add new club <strong>{createForm.clubName}</strong> (ID:{" "}
          {createForm.clubId}) with {newColors.length} color
          {newColors.length !== 1 ? "s" : ""}:
        </p>
        {COLOR_KEYS.map((key, i) =>
          isValidColor(createColors[key]) ? (
            <div
              key={key}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                padding: "8px 0",
                borderBottom: "1px solid #f0f0f0",
              }}
            >
              <span
                style={{
                  width: 56,
                  flexShrink: 0,
                  color: "#555",
                  fontSize: 13,
                }}
              >
                Color {i + 1}
              </span>
              <Swatch color={createColors[key]} size={24} />
              <span
                style={{
                  fontSize: 12,
                  color: "#888",
                  fontFamily: "monospace",
                }}
              >
                {rgbToHex(createColors[key]).toUpperCase()}
              </span>
              <span
                style={{
                  fontSize: 12,
                  color: "#aaa",
                  fontFamily: "monospace",
                }}
              >
                rgb({createColors[key].r}, {createColors[key].g},{" "}
                {createColors[key].b})
              </span>
            </div>
          ) : null,
        )}
      </Modal>
    </SuperAdminContainer>
  );
};

export default Colors;
